import type { FC, ReactElement, ReactNode } from 'react';
import { isValidElement, useCallback, useEffect, useMemo } from 'react';

import { useUpdateFormFieldPositions } from '@/hooks/mutations/form-field';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import useControlledCustomAttributes from '../../custom-attributes/useControlledCustomAttributes';
import { useCustomAttributeFileSave } from '../../custom-attributes/useCustomAttributeFileSave';
import { useCustomisableFormDataContext } from '../customisable-form-data/CustomisableFormDataContext';
import { useFormFieldElementsStore } from '../stores/useFormFieldElementsStore';
import { useFormFieldRegistryStore } from '../stores/useFormFieldRegistryStore';
import type { DefaultSectionProps } from './DefaultSection';
import DefaultSection from './DefaultSection';
import type { DefaultSectionConfig } from './hooks/useSectionManager';
import { useSectionManager } from './hooks/useSectionManager';
import { useTransformSchemaToRequiredFields } from './hooks/useTransformSchemaToRequiredFields';
import { useRiskSmartForm } from './RiskSmartFormContext';
import { SectionedFormLayout } from './SectionedFormLayout';

type FieldElement = ReactElement<Record<string, unknown>>;
type DefaultSectionElement = ReactElement<DefaultSectionProps>;

const isDefaultSectionElement = (
  element: ReactElement
): element is DefaultSectionElement => element.type === DefaultSection;

const childKeyError = (element: ReactElement): string =>
  `All children of a CustomisableForm must have a unique 'key' prop, ${
    (element.props as { name?: string }).name ?? 'a child'
  } does not have one.`;

const elementsToFields = (elements: ReactElement[]): FieldElement[] =>
  (elements as FieldElement[]).map((element) => {
    if (!element.key) {
      throw new Error(childKeyError(element));
    }

    return element;
  });

/**
 * Walks the top-level children of `CustomisableFieldWrapper` and partitions
 * them into a flat field-element list and (optionally) a synthesised
 * `defaultSections` array derived from `<DefaultSection>` markers.
 *
 * Top-level children must be either all `<DefaultSection>` markers OR all
 * raw field elements — mixing the two throws.
 */
const partitionChildren = (
  children: ReactNode[]
): {
  fieldElements: FieldElement[];
  defaultSections: DefaultSectionConfig[] | undefined;
} => {
  const validChildren = children.filter(isValidElement) as ReactElement[];
  if (validChildren.length === 0) {
    return { fieldElements: [], defaultSections: undefined };
  }

  const hasDefaultSection = validChildren.some(isDefaultSectionElement);
  if (!hasDefaultSection) {
    return {
      fieldElements: elementsToFields(validChildren),
      defaultSections: undefined,
    };
  }

  const allDefaultSections = validChildren.every(isDefaultSectionElement);
  if (!allDefaultSections) {
    throw new Error(
      'CustomisableFieldWrapper children must be either all <DefaultSection> ' +
        'elements or all field elements — mixing the two at the top level is ' +
        'not supported.'
    );
  }

  const defaultSections: DefaultSectionConfig[] = [];
  const fieldElements: FieldElement[] = [];

  for (const sectionElement of validChildren as DefaultSectionElement[]) {
    if (!sectionElement.key) {
      throw new Error(
        '<DefaultSection> must have a stable `key` prop — it is used as the ' +
          'SectionId. Prefix with `default_<entity>_` to avoid collisions.'
      );
    }

    const sectionChildren = sectionElement.props.children;
    const sectionChildrenArray = Array.isArray(sectionChildren)
      ? sectionChildren
      : [sectionChildren];
    const sectionFieldElements = elementsToFields(
      sectionChildrenArray.filter(isValidElement) as ReactElement[]
    );

    fieldElements.push(...sectionFieldElements);
    defaultSections.push({
      SectionId: String(sectionElement.key),
      Label: sectionElement.props.label,
      Description: sectionElement.props.description,
      fields: sectionFieldElements.map((el) => String(el.key)),
    });
  }

  return { fieldElements, defaultSections };
};

type Props = {
  children: ReactNode | ReactNode[];
  readOnly?: boolean;
};

/**
 * Wraps a forms fields to support customisation of the order
 * @returns
 */
const CustomisableFieldWrapper: FC<Props> = ({
  children: childrenProp,
  readOnly,
}) => {
  const { parentType, editMode } = useRiskSmartForm();
  const formSectionsEnabled = useIsFeatureFlagEnabled('dynamic_forms');

  if (!parentType) {
    throw new Error(
      'CustomisableForm must have a parentType on the RiskSmartFormProvider'
    );
  }

  const { updateFormFieldPositions } = useUpdateFormFieldPositions();

  const customAttributeElements = useControlledCustomAttributes({
    readOnly,
  });

  // Registers the submit-time upload/link pipeline for any "Attach file"
  // custom fields on this form (no-op when there are none).
  useCustomAttributeFileSave();

  const { formFieldOrdering, customAttributeSchema, formSections } =
    useCustomisableFormDataContext();
  const childrenArray = Array.isArray(childrenProp)
    ? childrenProp
    : [childrenProp];

  const { fieldElements, defaultSections } = partitionChildren(childrenArray);

  // Custom attributes are appended after default-section fields; they remain
  // unmapped from any default section and fall into useSectionManager's
  // unmapped-field bucket (appended to the last section).
  const customAttributeFieldElements = elementsToFields(
    customAttributeElements.filter(isValidElement) as ReactElement[]
  );
  const allFieldElements = [...fieldElements, ...customAttributeFieldElements];

  const elementsByKey = allFieldElements.map((element) => ({
    key: String(element.key),
    value: element,
  }));

  // Publish field elements + hardcoded default sections to the shared store
  // for preview access (form editor only).
  const setFieldElements = useFormFieldElementsStore((s) => s.setElements);
  const setDefaultSections = useFormFieldElementsStore(
    (s) => s.setDefaultSections
  );
  const clearFieldElements = useFormFieldElementsStore((s) => s.clear);
  useEffect(() => {
    if (!formSectionsEnabled) {
      return;
    }
    setFieldElements(elementsByKey);
    setDefaultSections(defaultSections ?? []);

    return () => clearFieldElements();
  }, [
    elementsByKey,
    defaultSections,
    setFieldElements,
    setDefaultSections,
    clearFieldElements,
    formSectionsEnabled,
  ]);

  // Register field IDs in the field registry store (form editor only)
  const registerField = useFormFieldRegistryStore(
    (state) => state.registerField
  );
  const unregisterField = useFormFieldRegistryStore(
    (state) => state.unregisterField
  );

  const customAttributeKeys = useMemo(
    () =>
      new Set(
        (
          customAttributeElements.filter((c) =>
            isValidElement(c)
          ) as ReactElement[]
        ).map((el) => String(el.key))
      ),
    [customAttributeElements]
  );

  useEffect(() => {
    if (!formSectionsEnabled) {
      return;
    }
    const fieldIds = elementsByKey.map((e) => e.key);
    for (const fieldId of fieldIds) {
      registerField(fieldId, {
        fieldId,
        label: fieldId,
        isCustom: customAttributeKeys.has(fieldId),
        defaultRequired: false,
        forcedRequired: false,
      });
    }

    return () => {
      for (const fieldId of fieldIds) {
        unregisterField(fieldId);
      }
    };
  }, [
    elementsByKey,
    registerField,
    unregisterField,
    customAttributeKeys,
    formSectionsEnabled,
  ]);

  useTransformSchemaToRequiredFields(parentType);

  // Use section manager for sectioned layout. Hardcoded `<DefaultSection>`
  // defaults only apply when sectioned rendering is enabled — with the flag
  // off the form is a flat legacy list, so collapse to a single section.
  // Otherwise the synthetic section boundaries would split the flat list and
  // reject drag-reorders that cross them (fields snap back on drop).
  const sectionManager = useSectionManager(
    elementsByKey,
    formSections ?? null,
    formFieldOrdering ?? undefined,
    formSectionsEnabled ? defaultSections : undefined
  );

  const isUpdate = !!customAttributeSchema;

  const saveFields = useCallback(async () => {
    if (!parentType) {
      return;
    }

    const elementsOrder = sectionManager.sections.flatMap(
      (section) => section.fields
    );

    await updateFormFieldPositions({
      parentType,
      ordering: elementsOrder.map((key, i) => ({
        FieldId: key,
        Position: i,
      })),
      isUpdate,
    });
  }, [sectionManager.sections, parentType, updateFormFieldPositions, isUpdate]);

  const { setOnSave } = useRiskSmartForm();

  useEffect(() => {
    if (editMode) {
      setOnSave(() => saveFields);
    }

    return () => setOnSave(undefined);
  }, [saveFields, setOnSave, editMode]);

  return (
    <div data-testid={'customisable-form-content'}>
      <SectionedFormLayout
        sections={sectionManager.sections}
        draggedFieldId={sectionManager.draggedFieldId}
        handleFieldReorderInSection={sectionManager.handleFieldReorderInSection}
        setDraggedFieldId={sectionManager.setDraggedFieldId}
        fieldComponents={elementsByKey}
        isEditMode={editMode}
        isReadOnly={readOnly}
        flatLayout={!formSectionsEnabled}
      />
    </div>
  );
};

export default CustomisableFieldWrapper;
